/**
 * Cabinet placement for the room planner (duplicate / re-seat flows).
 *
 * ## Floor cabinets (bases, tall units)
 * Search order: same Z row to +X of the source, then additional rows stepping +world Z by cabinet
 * depth (or `DUPLICATE_FLOOR_ROW_Z_OFFSET` fallback). If +X runs are exhausted, mirror with −X from
 * the right wall, then try −Z “back” rows. Collisions use full 3D axis-aligned bounds so a tall
 * floor unit cannot intersect an upper cabinet in height.
 *
 * ## Uppers (wall-mounted)
 * Clamp to the wall behind the source, slide along the wall until free, then advance to the next
 * wall clockwise when blocked (`getNextWallOnRight`), optionally stepping up rows by cabinet height.
 *
 * ## L-shaped rooms
 * Placement bounds merge Redux leg dimensions with scene meshes (aligned with `createLShapedFloor`).
 * The void in the L is rejected with a 2D footprint test in shape space; the floor mesh AABB alone
 * would still include that void.
 */
import type {MutableRefObject} from 'react';
import * as THREE from 'three';
import {
    CabinetMesh,
    RoomPlannerProductWithTemplate,
    RoomType,
} from 'components/customer/RoomPlanner/types';
import {ComputedDimension} from 'components/customer/RoomPlanner/lib/useRoomPlanner';
import {deleteCabinetMesh} from 'components/customer/RoomPlanner/helpers/deleteCabinetMesh';
import Camera from 'components/customer/RoomPlanner/lib/use3DRPCameraSetup';
import {autoRotateClamping} from 'components/customer/RoomPlanner/helpers/boundaries/autoRotate';
import {getWallBehindCabinet} from 'components/customer/RoomPlanner/helpers/boundaries/walls';
import {
    computeWorldBoundsExcludingAnimatedDoorParts,
    getDirection,
    getGroupWidth,
} from 'components/customer/RoomPlanner/helpers/calculations';
import {getRoomDimensionsComputed} from 'components/customer/RoomPlanner/helpers/getComputedDimension';
import {
    getLShapedFloorLayoutParams,
    isLShapedFootprintFullyInsideFloor,
    type LShapedFloorLayoutParams,
} from 'components/customer/RoomPlanner/helpers/roomFloorFootprint';
import ObjectNameMap from 'components/customer/RoomPlanner/constants/ObjectNameMap';
import productEvents from 'components/customer/RoomPlanner/lib/useProductEvents';

/** Minimum gap between placed cabinets (world units). */
export const DUPLICATE_PLACEMENT_GAP = 0.001;

/**
 * Deep-copy product values so duplicate placement never mutates the source cabinet.
 * @param {RoomPlannerProductWithTemplate} values Source cabinet product payload.
 * @return {RoomPlannerProductWithTemplate} Detached copy safe to mutate.
 */
const cloneDuplicateProductValues = (
    values: RoomPlannerProductWithTemplate
): RoomPlannerProductWithTemplate => {
    if (typeof structuredClone === 'function') {
        return structuredClone(values);
    }
    return JSON.parse(JSON.stringify(values)) as RoomPlannerProductWithTemplate;
};

/**
 * Extra inset on the placement AABB for **rectangular** duplicate search only.
 * Negative values widen usable X/Z slightly (e.g. `-0.001` near side walls).
 * Do not rely on this for L-shaped rooms — use `DUPLICATE_L_SHAPE_FLOOR_BOUNDS_PADDING`.
 */
export const DUPLICATE_FLOOR_BOUNDS_PADDING = -0.001;

/**
 * Extra slack on the L walkable footprint test (shape/world units). Use when duplicates beside a wall
 * fail despite free space. When `0` and `DUPLICATE_FLOOR_BOUNDS_PADDING` is negative, tolerance
 * auto-matches `-padding`.
 */
export const DUPLICATE_L_SHAPE_FOOTPRINT_TOLERANCE = 0;

/**
 * X-axis inset from nominal room width (Redux `rectangularDimension` → `getRoomDimensionsComputed().roomWidth`).
 * Keep in sync with `cabinetSpawn` (`wallMargin`) and `repositionCabinetsOutsideRoom`.
 */
export const DUPLICATE_FLOOR_WALL_MARGIN = 36;

/**
 * Fallback Z step between floor-duplicate rows when `getGroupWidth(cabinet).depth` is missing
 * or invalid (primary step is duplicate depth + gap).
 */
export const DUPLICATE_FLOOR_ROW_Z_OFFSET = 300;

/** When sliding a cabinet along X we stop if movement is below this threshold (stuck / numeric noise). */
const NEGLIGIBLE_TRANSLATION_EPSILON = 0.0001;

/** If wall-packing cannot move the cabinet at least this far on the slide axis, treat as stuck. */
const WALL_PACK_NO_PROGRESS_EPSILON = 0.001;

/**
 * Scratch vectors for wall-center math in `getNextWallOnRight` (avoids allocating per wall mesh).
 * Not safe across overlapping async calls; room planner uses this synchronously on the main thread.
 */
const wallNavScratchCenterA = new THREE.Vector3();
const wallNavScratchCenterB = new THREE.Vector3();
const wallNavScratchDelta = new THREE.Vector3();

/**
 * Outer bounding rectangle in world XZ for the L footprint (used to widen placement bounds).
 *
 * @param {LShapedFloorLayoutParams} layout L layout params.
 * @return {Object} Axis-aligned extents: minX, maxX, minZ, maxZ in world space.
 */
const getLShapedOuterWorldXZExtents = (
    layout: LShapedFloorLayoutParams
): {minX: number; maxX: number; minZ: number; maxZ: number} => {
    const outerPolygonShapeSpace: Array<[number, number]> = [
        [0, 0],
        [layout.leftDepthInner, 0],
        [layout.leftDepthInner, layout.legSplitY],
        [layout.rightWidthInner, layout.legSplitY],
        [layout.rightWidthInner, layout.topY],
        [0, layout.topY],
    ];

    let minX = Infinity;
    let maxX = -Infinity;
    let minZ = Infinity;
    let maxZ = -Infinity;

    for (const [sx, sy] of outerPolygonShapeSpace) {
        const wx = layout.positionX + sx;
        const wz = layout.positionZ - sy;
        minX = Math.min(minX, wx);
        maxX = Math.max(maxX, wx);
        minZ = Math.min(minZ, wz);
        maxZ = Math.max(maxZ, wz);
    }

    return {minX, maxX, minZ, maxZ};
};

/** Horizontal placement axis in the room floor plane (Y is vertical). */
type Axis = 'x' | 'z';

/** Ordinal wall identity for rectangular / L-shaped room walls we can snap uppers to. */
type WallKey = 'back' | 'right' | 'front' | 'left';

/** Slide direction along a wall: which world axis moves, sign, and unit step vector. */
type Movement = ReturnType<typeof getMovementFromDirection>;

/**
 * Wraps `angle` into [0, 2π) so wall-relative cabinet rotations stay comparable across hops.
 *
 * @param {number} angle Radians (typically cabinet.rotation.z vs wall baseline).
 * @return {number} Equivalent angle in [0, 2π).
 */
const normalizeAngle = (angle: number): number => {
    return ((angle % (Math.PI * 2)) + Math.PI * 2) % (Math.PI * 2);
};

const readHorizontalComponent = (vector: THREE.Vector3, axis: Axis) => {
    return axis === 'x' ? vector.x : vector.z;
};

const writeHorizontalComponent = (
    vector: THREE.Vector3,
    axis: Axis,
    value: number
) => {
    if (axis === 'x') {
        vector.x = value;
    } else {
        vector.z = value;
    }
};

const readBoundsMinOnHorizontalAxis = (box: THREE.Box3, axis: Axis) => {
    return axis === 'x' ? box.min.x : box.min.z;
};

const readBoundsMaxOnHorizontalAxis = (box: THREE.Box3, axis: Axis) => {
    return axis === 'x' ? box.max.x : box.max.z;
};

/**
 * Strict 3D overlap test for cabinet meshes (includes height so bases vs uppers cannot intersect).
 *
 * @param {THREE.Box3} boxA First axis-aligned bounds in world space.
 * @param {THREE.Box3} boxB Second axis-aligned bounds in world space.
 * @param {number} epsilon Minimum separation treated as “touching, not overlapping”.
 * @return {boolean} True when volumes intersect beyond epsilon.
 */
const boundingBoxesOverlap3D = (
    boxA: THREE.Box3,
    boxB: THREE.Box3,
    epsilon = DUPLICATE_PLACEMENT_GAP
) => {
    return (
        boxA.min.x < boxB.max.x - epsilon &&
        boxA.max.x > boxB.min.x + epsilon &&
        boxA.min.y < boxB.max.y - epsilon &&
        boxA.max.y > boxB.min.y + epsilon &&
        boxA.min.z < boxB.max.z - epsilon &&
        boxA.max.z > boxB.min.z + epsilon
    );
};

/**
 * Collision AABB for duplicate packing. Placeholders use mesh-only bounds so
 * overlay children do not hide or inflate the footprint.
 * @param {CabinetMesh} item Cabinet or placeholder
 * @return {THREE.Box3} World-space bounds
 */
const getLayoutCollisionBox = (item: CabinetMesh): THREE.Box3 => {
    if (item.userData?.isPlaceholder) {
        const box = computeWorldBoundsExcludingAnimatedDoorParts(item);
        if (
            !box.isEmpty() &&
            Number.isFinite(box.min.x) &&
            Number.isFinite(box.max.x)
        ) {
            return box;
        }
    }
    return new THREE.Box3().setFromObject(item);
};

/**
 * Picks dominant horizontal slide axis from a start/end pair (used when upper row reuses source XZ).
 *
 * @param {THREE.Vector3} from Prior position (world).
 * @param {THREE.Vector3} to Target position (world).
 * @param {string} [fallbackDirection] Passed to `getMovementFromDirection` when delta is degenerate.
 * @return {Movement} Axis-aligned slide descriptor for wall packing.
 */
const getMovementFromDelta = (
    from: THREE.Vector3,
    to: THREE.Vector3,
    fallbackDirection?: string
): Movement => {
    const deltaX = to.x - from.x;
    const deltaZ = to.z - from.z;
    const epsilon = DUPLICATE_PLACEMENT_GAP;

    if (Math.abs(deltaX) >= Math.abs(deltaZ) && Math.abs(deltaX) > epsilon) {
        return {
            axis: 'x',
            sign: deltaX >= 0 ? 1 : -1,
            vector: new THREE.Vector3(deltaX >= 0 ? 1 : -1, 0, 0),
        };
    }

    if (Math.abs(deltaZ) > epsilon) {
        return {
            axis: 'z',
            sign: deltaZ >= 0 ? 1 : -1,
            vector: new THREE.Vector3(0, 0, deltaZ >= 0 ? 1 : -1),
        };
    }

    return getMovementFromDirection(fallbackDirection);
};

/**
 * Maps user-facing `currentDirection` strings to the world axis we slide along when packing on a wall.
 * Default / `'front'` → +X; `'back'` → −X; `'right'` → +Z; `'left'` → −Z.
 *
 * @param {string} [direction] Cabinet `userData.currentDirection` (defaults to front/+X behavior).
 * @return {Movement} Axis, sign, and unit step for repeated collision resolution.
 */
export const getMovementFromDirection = (direction?: string) => {
    if (direction === 'right') {
        return {
            axis: 'z' as const,
            sign: 1,
            vector: new THREE.Vector3(0, 0, 1),
        };
    }

    if (direction === 'left') {
        return {
            axis: 'z' as const,
            sign: -1,
            vector: new THREE.Vector3(0, 0, -1),
        };
    }

    if (direction === 'back') {
        return {
            axis: 'x' as const,
            sign: -1,
            vector: new THREE.Vector3(-1, 0, 0),
        };
    }

    return {
        axis: 'x' as const,
        sign: 1,
        vector: new THREE.Vector3(1, 0, 0),
    };
};

/** Injected to avoid `cabinetPlacement` ↔ `renderCabinetGroup` circular imports. */
export type RenderCabinetGroupForDuplicateFn = (
    product: RoomPlannerProductWithTemplate,
    dimensions: ComputedDimension,
    room: string,
    cabinetGroupListRef: MutableRefObject<CabinetMesh[]>,
    row?: number,
    tempMesh?: boolean
) => {widthIncrement: number; cabinetGroup: CabinetMesh};

/**
 * Which slide directions to try when duplicating (uppers: along facing; floors: front/+X only).
 *
 * @param {boolean} isUpperProduct Whether the source is wall-mounted.
 * @param {string | undefined} currentDirection Source `userData.currentDirection`.
 * @return {string[]} Direction keys for `offsetPositionBesideSource` / placement.
 */
const duplicateSlideDirections = (
    isUpperProduct: boolean,
    currentDirection: string | undefined
): string[] => (isUpperProduct ? [currentDirection || 'front'] : ['front']);

/**
 * Writes position one cabinet span (+ gap) beside the source along `direction`.
 *
 * @param {CabinetMesh} source Source cabinet mesh.
 * @param {THREE.Vector3} out Output position (mutated).
 * @param {string} direction `currentDirection` axis (front/back/left/right).
 * @return {void}
 */
const offsetPositionBesideSource = (
    source: CabinetMesh,
    out: THREE.Vector3,
    direction: string
): void => {
    const movement = getMovementFromDirection(direction);
    const box = new THREE.Box3().setFromObject(source);
    const span =
        movement.axis === 'x' ? box.max.x - box.min.x : box.max.z - box.min.z;
    out.copy(source.position);
    out.addScaledVector(movement.vector, span + DUPLICATE_PLACEMENT_GAP);
};

/**
 * Tries to place a duplicate cabinet beside the source, along each valid slide direction.
 *
 * @param {CabinetMesh} sourceCabinet Cabinet being duplicated.
 * @param {ComputedDimension} dimensions Room planner dimensions for render.
 * @param {string} room Room key for render.
 * @param {import('react').MutableRefObject<CabinetMesh[]>} cabinetGroupListRef Scene cabinet list ref.
 * @param {string} roomType RECTANGULAR or LSHAPED.
 * @param {number} roomCabinetNumber Provisional mesh id before API duplicate.
 * @param {number} cloneIndex Duplicate sub-index for `values.id`.
 * @param {number} roomId Room id for userData.
 * @param {RenderCabinetGroupForDuplicateFn} renderCabinetGroup Injected `renderCabinetGroup` (avoids circular import).
 * @return {CabinetMesh | null} Placed mesh or null if no slot found.
 */

export const tryPlaceDuplicateClone = (
    sourceCabinet: CabinetMesh,
    dimensions: ComputedDimension,
    room: string,
    cabinetGroupListRef: MutableRefObject<CabinetMesh[]>,
    roomType: string,
    roomCabinetNumber: number,
    cloneIndex: number,
    roomId: number,
    renderCabinetGroup: RenderCabinetGroupForDuplicateFn
): CabinetMesh | null => {
    const duplicateValues = cloneDuplicateProductValues(
        sourceCabinet.userData.values as RoomPlannerProductWithTemplate
    );
    duplicateValues.job_cabinet_id = -1;
    duplicateValues.id = `${roomCabinetNumber}-${cloneIndex}`;
    duplicateValues.duplicate_id = `${roomCabinetNumber}-${cloneIndex}`;
    duplicateValues.room_cab_number = roomCabinetNumber;
    duplicateValues.room_id = roomId;

    const isUpper = Boolean(sourceCabinet.userData.isUpperProduct);

    const trialPosition = new THREE.Vector3();
    const beside = new THREE.Vector3();
    const directions = duplicateSlideDirections(
        isUpper,
        sourceCabinet.userData.currentDirection
    );

    for (const direction of directions) {
        if (isUpper) {
            offsetPositionBesideSource(sourceCabinet, beside, direction);
        } else {
            beside.copy(sourceCabinet.position);
        }
        trialPosition.copy(beside);
        trialPosition.y = sourceCabinet.position.y;

        const {cabinetGroup: rendered} = renderCabinetGroup(
            duplicateValues,
            {
                ...dimensions,
                positionX: trialPosition.x + 40,
                defaultPositionZ: trialPosition.z,
            },
            room,
            cabinetGroupListRef
        );

        rendered.position.copy(trialPosition);
        rendered.rotation.copy(sourceCabinet.rotation);
        rendered.userData.currentDirection =
            sourceCabinet.userData.currentDirection;

        const existingCabinets = cabinetGroupListRef.current.filter(
            (cab) => cab !== rendered && !cab.userData.isTempMesh && cab.visible
        );
        const placeholders = productEvents
            .getLayoutMeshes()
            .filter(
                (mesh) =>
                    Boolean(mesh.userData.isPlaceholder) &&
                    mesh !== rendered &&
                    !mesh.userData.isTempMesh &&
                    mesh.visible !== false
            );
        const existing = [...existingCabinets, ...placeholders];

        const placed = placeCabinetWithWallFallback({
            cabinet: rendered,
            sourceCabinet,
            existingCabinets: existing,
            scene: Camera.scene,
            roomType,
            firstRowStartPosition: trialPosition.clone(),
            allowUpperRows: true,
            wallHeight: dimensions.wallHeight,
        });

        if (placed) {
            return rendered;
        }

        deleteCabinetMesh(rendered, false, cabinetGroupListRef);
    }

    return null;
};

/**
 * When the cabinet sits on a known wall, slide along that wall instead of using a raw direction guess.
 *
 * @param {WallKey | null} wallKey Which perimeter wall the upper is on, if recognized.
 * @param {Movement} fallback Used when `wallKey` is null or unmapped.
 * @return {Movement} Axis-aligned slide for `resolvePositionOnWall`.
 */
const getPlacementMovementForWallKey = (
    wallKey: WallKey | null,
    fallback: Movement
): Movement => {
    if (wallKey === 'back') {
        return getMovementFromDirection('front');
    }
    if (wallKey === 'right') {
        return getMovementFromDirection('right');
    }
    if (wallKey === 'left') {
        return getMovementFromDirection('left');
    }
    if (wallKey === 'front') {
        return getMovementFromDirection('back');
    }
    return fallback;
};

/**
 * Next segment name clockwise on the L perimeter (`createLShapedWalls` mesh `.name` / `userData.id`).
 * Implemented as a switch so lookup is not a dynamic property access (object injection lint).
 *
 * @param {string} segment Current wall segment id.
 * @return {string | undefined} Following segment id, or undefined if unknown.
 */
const getNextLShapeWallSegmentName = (segment: string): string | undefined => {
    switch (segment) {
        case 'rightWidthWall':
            return 'rightDepthWall';
        case 'rightDepthWall':
            return 'rightWidth2Wall';
        case 'rightWidth2Wall':
            return 'leftWidth2Wall';
        case 'leftWidth2Wall':
            return 'leftDepthWall';
        case 'leftDepthWall':
            return 'leftWidthWall';
        case 'leftWidthWall':
            return 'rightWidthWall';
        default:
            return undefined;
    }
};

const getWallRotation = (wall?: THREE.Mesh | null): number | null => {
    if (!wall) return null;
    const wallName = wall.name || String(wall.userData?.id || '');

    switch (wallName) {
        case 'wall:back':
        case 'backWall':
        case 'rightWidthWall':
            return 0;
        case 'wall:front':
        case 'frontWall':
        case 'leftDepthWall':
        case 'rightWidth2Wall':
            return Math.PI;
        case 'wall:left':
        case 'leftWall':
        case 'leftWidthWall':
            return Math.PI / 2;
        case 'wall:right':
        case 'rightWall':
        case 'rightDepthWall':
        case 'leftWidth2Wall':
            return -Math.PI / 2;
        default:
            return null;
    }
};

const mapRotationToCurrentDirection = (rotation: number): string => {
    const direction = getDirection(rotation);

    switch (direction) {
        case 'right':
            return 'left';
        case 'left':
            return 'right';
        case 'back':
            return 'back';
        default:
            return 'front'; // Default to front/+X behavior.
    }
};

/** Wall quads live under a single group per room shape (`3d:room:wall:*`).
 * Returns an array of wall meshes for the current room shape.
 *
 * @param {THREE.Scene} scene Three.js scene.
 * @return {THREE.Mesh[]} Array of wall meshes.
 */
const getWallMeshes = (scene: THREE.Scene): THREE.Mesh[] => {
    const wallGroup = scene.children.find(
        (ch) =>
            ch.name === '3d:room:wall:rectangular' ||
            ch.name === '3d:room:wall:lshape'
    ) as THREE.Group | undefined;

    if (!wallGroup) return [];

    return wallGroup.children.filter((child) => child instanceof THREE.Mesh) as
        THREE.Mesh[] | [];
};

/**
 * Maps a wall mesh to a semantic perimeter key (slide / hop logic). L-shaped segment names align
 * with `getWallRotation` (e.g. `rightWidthWall` → back).
 *
 * @param {THREE.Mesh | null} wall Wall mesh.
 * @param {string} [roomType] When `LSHAPED`, resolves `leftWidthWall` / `rightWidth2Wall` / …
 * @return {WallKey | null} Semantic key, or null if unknown.
 */
const getWallKey = (
    wall: THREE.Mesh | null,
    roomType?: string
): WallKey | null => {
    if (!wall) return null;
    const name = wall.name || String(wall.userData?.id || '');

    if (roomType === 'LSHAPED') {
        switch (name) {
            case 'rightWidthWall':
                return 'back';
            case 'leftDepthWall':
            case 'rightWidth2Wall':
                return 'front';
            case 'leftWidthWall':
                return 'left';
            case 'rightDepthWall':
            case 'leftWidth2Wall':
                return 'right';
            default:
                break;
        }
    }

    if (name === 'wall:back' || name === 'backWall') return 'back';
    if (name === 'wall:right' || name === 'rightWall') return 'right';
    if (name === 'wall:front' || name === 'frontWall') return 'front';
    if (name === 'wall:left' || name === 'leftWall') return 'left';

    return null;
};

const findWallByKey = (
    key: WallKey,
    walls: THREE.Mesh[]
): THREE.Mesh | null => {
    let targetNames: string[] = [];
    switch (key) {
        case 'back':
            targetNames = ['wall:back', 'backWall'];
            break;
        case 'right':
            targetNames = ['wall:right', 'rightWall'];
            break;
        case 'front':
            targetNames = ['wall:front', 'frontWall'];
            break;
        case 'left':
            targetNames = ['wall:left', 'leftWall'];
            break;
    }

    return (
        walls.find((wall) => {
            const wallName = wall.name || String(wall.userData?.id || '');
            return targetNames.includes(wallName);
        }) ?? null
    );
};

/**
 * Upper cabinets “walk” clockwise around the room when blocked; this returns the next wall mesh.
 * Named walls use a fixed back→right→front→left order; otherwise we pick the nearest wall ahead
 * of the movement vector (same intent for L-shaped name variants).
 *
 * @param {THREE.Mesh | null} currentWall Prior wall (world).
 * @param {THREE.Vector3} movementVector Slide vector (world).
 * @param {THREE.Mesh[]} wallMeshes All wall quads in the room.
 * @param {string} roomType RECTANGULAR or LSHAPED.
 * @return {THREE.Mesh | null} Next wall (world).
 */
const getNextWallOnRight = (
    currentWall: THREE.Mesh | null,
    movementVector: THREE.Vector3,
    wallMeshes: THREE.Mesh[],
    roomType: string
): THREE.Mesh | null => {
    if (!currentWall || !wallMeshes.length) return null;

    if (roomType === 'LSHAPED') {
        const seg = currentWall.name || String(currentWall.userData?.id || '');
        const nextName = getNextLShapeWallSegmentName(seg);
        if (nextName) {
            const nextWall =
                wallMeshes.find((w) => {
                    const n = w.name || String(w.userData?.id || '');
                    return n === nextName;
                }) ?? null;
            if (nextWall) return nextWall;
        }
    }

    const currentKey = getWallKey(currentWall, roomType);
    if (currentKey) {
        let nextKey: WallKey;
        switch (currentKey) {
            case 'back':
                nextKey = 'right';
                break;
            case 'right':
                nextKey = 'front';
                break;
            case 'front':
                nextKey = 'left';
                break;
            default:
                nextKey = 'back';
                break;
        }
        const nextWall = findWallByKey(nextKey, wallMeshes);
        if (nextWall) return nextWall;
    }

    new THREE.Box3()
        .setFromObject(currentWall)
        .getCenter(wallNavScratchCenterA);

    let closestWallAhead: THREE.Mesh | null = null;
    let closestDistanceSq = Infinity;

    wallMeshes.forEach((candidateWall) => {
        if (candidateWall === currentWall) return;

        new THREE.Box3()
            .setFromObject(candidateWall)
            .getCenter(wallNavScratchCenterB);

        wallNavScratchDelta.subVectors(
            wallNavScratchCenterB,
            wallNavScratchCenterA
        );
        if (wallNavScratchDelta.dot(movementVector) <= 0) return;

        const distanceSq = wallNavScratchDelta.lengthSq();
        if (distanceSq < closestDistanceSq) {
            closestDistanceSq = distanceSq;
            closestWallAhead = candidateWall;
        }
    });

    return closestWallAhead;
};

/**
 * Iteratively clamps an upper to its wall, then nudges along `movement` until free or stuck.
 *
 * @return {boolean} True when a non-overlapping position was found within iteration budget.
 */
const resolvePositionOnWall = ({
    cabinet,
    existingCabinets,
    wall,
    roomType,
    isUpperProduct,
    movementOverride,
}: {
    cabinet: CabinetMesh;
    existingCabinets: CabinetMesh[];
    wall: THREE.Mesh | null;
    roomType: string;
    isUpperProduct: boolean;
    movementOverride?: Movement;
}) => {
    const movement =
        movementOverride ??
        getMovementFromDirection(cabinet.userData.currentDirection || 'front');
    const maxIterations = existingCabinets.length + 2;

    for (let iteration = 0; iteration < maxIterations; iteration++) {
        const {clamped} = autoRotateClamping(
            cabinet,
            wall,
            cabinet.position.clone(),
            isUpperProduct,
            roomType
        );

        cabinet.position.copy(clamped);
        cabinet.updateMatrixWorld(true);

        const movingCabinetBounds = new THREE.Box3().setFromObject(cabinet);
        const overlappingNeighborBounds: THREE.Box3[] = [];

        existingCabinets.forEach((neighborCabinet) => {
            if (
                neighborCabinet === cabinet ||
                neighborCabinet.userData.isTempMesh
            ) {
                return;
            }
            neighborCabinet.updateMatrixWorld(true);
            const neighborBounds = getLayoutCollisionBox(neighborCabinet);
            if (boundingBoxesOverlap3D(movingCabinetBounds, neighborBounds)) {
                overlappingNeighborBounds.push(neighborBounds);
            }
        });

        if (!overlappingNeighborBounds.length) return true;

        const movingMinOnAxis = readBoundsMinOnHorizontalAxis(
            movingCabinetBounds,
            movement.axis
        );
        const movingMaxOnAxis = readBoundsMaxOnHorizontalAxis(
            movingCabinetBounds,
            movement.axis
        );
        let blockingEdgeAlongAxis = movement.sign > 0 ? -Infinity : Infinity;

        overlappingNeighborBounds.forEach((neighborBounds) => {
            if (movement.sign > 0) {
                blockingEdgeAlongAxis = Math.max(
                    blockingEdgeAlongAxis,
                    readBoundsMaxOnHorizontalAxis(neighborBounds, movement.axis)
                );
            } else {
                blockingEdgeAlongAxis = Math.min(
                    blockingEdgeAlongAxis,
                    readBoundsMinOnHorizontalAxis(neighborBounds, movement.axis)
                );
            }
        });

        const nextPosition = cabinet.position.clone();
        if (movement.sign > 0) {
            const offset =
                blockingEdgeAlongAxis -
                movingMinOnAxis +
                DUPLICATE_PLACEMENT_GAP;
            writeHorizontalComponent(
                nextPosition,
                movement.axis,
                readHorizontalComponent(nextPosition, movement.axis) + offset
            );
        } else {
            const offset =
                movingMaxOnAxis -
                blockingEdgeAlongAxis +
                DUPLICATE_PLACEMENT_GAP;
            writeHorizontalComponent(
                nextPosition,
                movement.axis,
                readHorizontalComponent(nextPosition, movement.axis) - offset
            );
        }

        const previousAxis = readHorizontalComponent(
            cabinet.position,
            movement.axis
        );
        const nextAxis = readHorizontalComponent(nextPosition, movement.axis);
        if (Math.abs(previousAxis - nextAxis) < WALL_PACK_NO_PROGRESS_EPSILON) {
            return false;
        }

        cabinet.position.copy(nextPosition);
    }

    return false;
};

/**
 * After hopping to the next wall: match rotation to the new wall, step one cabinet width along the
 * slide vector, then run `resolvePositionOnWall` so we land flush without overlaps.
 *
 * @param {CabinetMesh} cabinet The cabinet to place.
 * @param {CabinetMesh[]} existingCabinets Existing cabinets in the room.
 * @param {THREE.Mesh | null} activeWall Prior wall (world).
 * @param {THREE.Mesh} nextWall Next wall (world).
 * @param {string} roomType Room type.
 * @param {number} targetRowY Target row Y.
 * @param {WallKey | null} sourceWallKey Source wall key.
 * @param {string} sourceDirection Source direction.
 * @return {Object} Placed status and next active wall.
 */
const tryPlaceAfterWallJump = ({
    cabinet,
    existingCabinets,
    activeWall,
    nextWall,
    roomType,
    targetRowY,
    sourceWallKey,
    sourceDirection,
    skipInitialOffset = false,
}: {
    cabinet: CabinetMesh;
    existingCabinets: CabinetMesh[];
    activeWall: THREE.Mesh | null;
    nextWall: THREE.Mesh;
    roomType: string;
    targetRowY: number;
    sourceWallKey: WallKey | null;
    sourceDirection: string;
    /** When true, don't pre-advance by one cabinet span after hopping walls.
     *  Use for brand-new cabinets that should start at the wall corner, not
     *  one width away (duplicates need the offset to avoid landing on the
     *  source cabinet). */
    skipInitialOffset?: boolean;
}): {placed: boolean; activeWall: THREE.Mesh} => {
    const currentWallRotation = getWallRotation(activeWall);
    const nextWallRotation = getWallRotation(nextWall);

    if (nextWallRotation !== null) {
        if (currentWallRotation !== null) {
            const relativeRotation = normalizeAngle(
                cabinet.rotation.z - currentWallRotation
            );
            cabinet.rotation.z = normalizeAngle(
                nextWallRotation + relativeRotation
            );
        } else {
            cabinet.rotation.z = normalizeAngle(nextWallRotation);
        }
    }

    cabinet.userData.currentWall = nextWall;

    const activeWallKey = getWallKey(activeWall, roomType);
    const nextWallKey = getWallKey(nextWall, roomType);
    const isBackToRightTransition =
        sourceWallKey === 'back' &&
        sourceDirection === 'right' &&
        activeWallKey === 'back' &&
        nextWallKey === 'right';

    if (isBackToRightTransition) {
        cabinet.userData.currentDirection = 'back';
    } else {
        cabinet.userData.currentDirection = mapRotationToCurrentDirection(
            cabinet.rotation.z
        );
    }

    const nextMovement = getMovementFromDirection(
        cabinet.userData.currentDirection
    );
    const jumpPlacementMovement = getPlacementMovementForWallKey(
        getWallKey(nextWall, roomType),
        nextMovement
    );
    const cabinetBoundsForSpan = new THREE.Box3().setFromObject(cabinet);
    const cabinetSpanAlongJumpAxis =
        jumpPlacementMovement.axis === 'x'
            ? cabinetBoundsForSpan.max.x - cabinetBoundsForSpan.min.x
            : cabinetBoundsForSpan.max.z - cabinetBoundsForSpan.min.z;

    if (!skipInitialOffset) {
        // For duplicates: step one cabinet span along the new wall so the
        // duplicate doesn't land on top of the source cabinet at the corner.
        // For new (non-duplicate) cabinets this is skipped so placement starts
        // flush at the corner with no artificial gap.
        cabinet.position.addScaledVector(
            jumpPlacementMovement.vector,
            cabinetSpanAlongJumpAxis + DUPLICATE_PLACEMENT_GAP
        );
    }
    cabinet.position.y = targetRowY;

    const placed = resolvePositionOnWall({
        cabinet,
        existingCabinets,
        wall: nextWall,
        roomType,
        isUpperProduct: Boolean(cabinet.userData.isUpperProduct),
        movementOverride: jumpPlacementMovement,
    });

    return {placed, activeWall: nextWall};
};

type PlaceCabinetWithWallFallbackParams = {
    cabinet: CabinetMesh;
    sourceCabinet: CabinetMesh;
    existingCabinets: CabinetMesh[];
    scene: THREE.Scene;
    roomType: string;
    firstRowStartPosition: THREE.Vector3;
    allowUpperRows?: boolean;
    wallHeight?: number;
};

const getWallBoundsXZ = (scene: THREE.Scene): THREE.Box3 | null => {
    const wallMeshes = getWallMeshes(scene);
    if (!wallMeshes.length) return null;

    const bounds = new THREE.Box3();
    wallMeshes.forEach((wall) => {
        const box = new THREE.Box3().setFromObject(wall);
        bounds.union(box);
    });

    return bounds.isEmpty() ? null : bounds;
};

const getFloorBoundsXZ = (scene: THREE.Scene): THREE.Box3 | null => {
    const floor =
        (scene.getObjectByName(ObjectNameMap.floor) as
            THREE.Mesh | undefined) ??
        (scene.getObjectByName(ObjectNameMap.lshapeFloor) as
            THREE.Mesh | undefined);

    if (!floor) return null;

    const bounds = new THREE.Box3().setFromObject(floor);
    return bounds.isEmpty() ? null : bounds;
};

/**
 * Widen X limits for rectangular rooms so duplicates use the same usable width as
 * `determineSpawnLocation` (floor meshes can be narrower than Redux room width at corners).
 *
 * @param {THREE.Box3 | null} bounds Floor or wall-union bounds from the scene.
 * @return {THREE.Box3 | null} Merged bounds, or unchanged / null when not applicable.
 */
const mergePlacementBoundsWithComputedRoomX = (
    bounds: THREE.Box3 | null
): THREE.Box3 | null => {
    const dims = getRoomDimensionsComputed();
    if (dims.roomType !== 'RECTANGULAR' || !bounds) {
        return bounds;
    }

    const minX = -dims.roomWidth / 2 + DUPLICATE_FLOOR_WALL_MARGIN;
    const maxX = dims.roomWidth / 2 - DUPLICATE_FLOOR_WALL_MARGIN;
    const merged = bounds.clone();
    merged.min.x = Math.min(merged.min.x, minX);
    merged.max.x = Math.max(merged.max.x, maxX);
    return merged;
};

/**
 * Merge floor/wall XZ bounds with Redux-driven L-shape outer extents (same vertices as
 * `createLShapedFloor` in `useRoomLayout`), so placement limits track edited dimensions.
 *
 * @param {THREE.Box3 | null} bounds Bounds from the l-shape floor mesh or wall union.
 * @return {THREE.Box3 | null} Merged bounds, or null only when L params missing and bounds null.
 */
const mergePlacementBoundsWithComputedLShape = (
    bounds: THREE.Box3 | null
): THREE.Box3 | null => {
    const lShapeLayout = getLShapedFloorLayoutParams();
    if (!lShapeLayout) {
        return bounds;
    }

    const {minX, maxX, minZ, maxZ} =
        getLShapedOuterWorldXZExtents(lShapeLayout);

    if (!bounds) {
        return new THREE.Box3(
            new THREE.Vector3(minX, -1e6, minZ),
            new THREE.Vector3(maxX, 1e6, maxZ)
        );
    }

    const merged = bounds.clone();
    merged.min.x = Math.min(merged.min.x, minX);
    merged.max.x = Math.max(merged.max.x, maxX);
    merged.min.z = Math.min(merged.min.z, minZ);
    merged.max.z = Math.max(merged.max.z, maxZ);
    return merged;
};

/**
 * Usable XZ footprint for floor duplicates: scene floor/wall meshes merged with Redux room shape
 * (rectangular X widen, L-shape outer hull) so bounds match spawn logic.
 *
 * @param {THREE.Scene} scene Active room planner scene graph.
 * @return {THREE.Box3 | null} Merged placement bounds, or null when no floor/walls found.
 */
export const getDuplicateFloorPlacementBounds = (
    scene: THREE.Scene
): THREE.Box3 | null => {
    const fromMeshes = getFloorBoundsXZ(scene) ?? getWallBoundsXZ(scene);
    const dims = getRoomDimensionsComputed();

    if (dims.roomType === 'RECTANGULAR') {
        return mergePlacementBoundsWithComputedRoomX(fromMeshes);
    }

    if (dims.roomType === 'LSHAPED') {
        return mergePlacementBoundsWithComputedLShape(fromMeshes);
    }

    return fromMeshes;
};

/**
 * Floor / base / tall duplicate: try world +X of the source (then further +Z rows by depth); if the
 * right side / forward (+Z) run is exhausted, try −X (left of source, then pack from the right
 * wall), then −Z (“back”) rows with the same left/right passes until room bounds are exhausted.
 * Collisions use full 3D AABB (`boundingBoxesOverlap3D`), including wall uppers, so tall floor units cannot
 * intersect upper (or other) cabinet boxes in height. L-shaped rooms: bounds merge with Redux
 * leg dimensions (same as `createLShapedFloor`), and the L cut-out is excluded via a footprint test
 * (mesh AABB alone would include the void).
 *
 * @return {boolean} Whether a non-overlapping position inside floor/wall bounds was found.
 */
export const duplicateFloorCabinet = ({
    cabinet,
    sourceCabinet,
    existingCabinets,
    scene,
}: {
    cabinet: CabinetMesh;
    sourceCabinet: CabinetMesh;
    existingCabinets: CabinetMesh[];
    scene: THREE.Scene;
}): boolean => {
    /**
     * L footprint tests only apply when `roomType === 'LSHAPED'`; cache avoids re-reading Redux
     * on every collision-free candidate inside `duplicateFloorCabinet`.
     */
    const cachedLShapeLayoutForFootprint = getLShapedFloorLayoutParams();

    cabinet.rotation.copy(sourceCabinet.rotation);
    cabinet.userData.currentDirection = sourceCabinet.userData.currentDirection;

    sourceCabinet.updateMatrixWorld(true);
    const sourceCabinetWorldBounds = new THREE.Box3().setFromObject(
        sourceCabinet
    );

    cabinet.position.copy(sourceCabinet.position);
    cabinet.updateMatrixWorld(true);
    const {depth: duplicateDepthFromProduct} = getGroupWidth(cabinet);
    const rowSpacingAlongWorldZ =
        (Number.isFinite(duplicateDepthFromProduct) &&
        duplicateDepthFromProduct > 0
            ? duplicateDepthFromProduct
            : DUPLICATE_FLOOR_ROW_Z_OFFSET) + DUPLICATE_PLACEMENT_GAP;

    const roomFloorPlacementBounds = getDuplicateFloorPlacementBounds(scene);
    const placementBoundsInset = DUPLICATE_FLOOR_BOUNDS_PADDING;
    const autoLShapeFootprintTolerance =
        placementBoundsInset < 0 ? -placementBoundsInset : 0;
    const lShapeFootprintTolerance = Math.max(
        DUPLICATE_L_SHAPE_FOOTPRINT_TOLERANCE,
        autoLShapeFootprintTolerance
    );
    const maxHorizontalSlideAttempts = Math.max(
        12,
        existingCabinets.length * 2 + 4
    );
    const maxWorldZRowAttempts = 25;

    const sourceWorldY = sourceCabinet.position.y;
    const sourceWorldZ = sourceCabinet.position.z;

    const zRowChangedTolerance = 0.01;

    /** Scratch AABB for the duplicate only (hot path: avoid allocating per slide). */
    const duplicateBoundsBox = new THREE.Box3();
    /** Neighbor AABBs frozen at entry — only `cabinet` moves during placement. */
    const occupantWorldBoxes: THREE.Box3[] = [];
    for (const other of existingCabinets) {
        if (other.userData.isTempMesh) continue;
        // Source is the anchor; treating it as an obstacle slides the duplicate into walls / void.
        if (other === sourceCabinet) continue;
        other.updateMatrixWorld(true);
        occupantWorldBoxes.push(getLayoutCollisionBox(other));
    }

    const room = roomFloorPlacementBounds;
    const pad = placementBoundsInset;
    const roomMinXEff = room ? room.min.x + pad : NaN;
    const roomMaxXEff = room ? room.max.x - pad : NaN;
    const roomMinZEff = room ? room.min.z + pad : NaN;
    const roomMaxZEff = room ? room.max.z - pad : NaN;
    // Wall-aligned source cabinets may already sit slightly past floor Z bounds.
    // Allow the duplicate to keep that same row before falling through to a new row.
    const sourceRoomMinZOverflow = room
        ? Math.max(0, roomMinZEff - sourceCabinetWorldBounds.min.z)
        : 0;
    const sourceRoomMaxZOverflow = room
        ? Math.max(0, sourceCabinetWorldBounds.max.z - roomMaxZEff)
        : 0;
    const duplicateRoomMinZEff =
        roomMinZEff - sourceRoomMinZOverflow - DUPLICATE_PLACEMENT_GAP;
    const duplicateRoomMaxZEff =
        roomMaxZEff + sourceRoomMaxZOverflow + DUPLICATE_PLACEMENT_GAP;

    const alignDuplicateMinXToWorldX = (targetWorldX: number) => {
        cabinet.updateMatrixWorld(true);
        duplicateBoundsBox.setFromObject(cabinet);
        cabinet.position.x += targetWorldX - duplicateBoundsBox.min.x;
        cabinet.updateMatrixWorld(true);
    };

    const alignDuplicateMaxXToWorldX = (targetWorldX: number) => {
        cabinet.updateMatrixWorld(true);
        duplicateBoundsBox.setFromObject(cabinet);
        cabinet.position.x += targetWorldX - duplicateBoundsBox.max.x;
        cabinet.updateMatrixWorld(true);
    };

    const placeDuplicateRightOfSourceOnRow = (rowWorldZ: number) => {
        cabinet.position.set(sourceCabinet.position.x, sourceWorldY, rowWorldZ);
        cabinet.updateMatrixWorld(true);
        duplicateBoundsBox.setFromObject(cabinet);
        cabinet.position.x +=
            sourceCabinetWorldBounds.max.x +
            DUPLICATE_PLACEMENT_GAP -
            duplicateBoundsBox.min.x;
        cabinet.updateMatrixWorld(true);
    };

    const placeDuplicateLeftOfSourceOnRow = (rowWorldZ: number) => {
        cabinet.position.set(sourceCabinet.position.x, sourceWorldY, rowWorldZ);
        cabinet.updateMatrixWorld(true);
        duplicateBoundsBox.setFromObject(cabinet);
        cabinet.position.x +=
            sourceCabinetWorldBounds.min.x -
            DUPLICATE_PLACEMENT_GAP -
            duplicateBoundsBox.max.x;
        cabinet.updateMatrixWorld(true);
    };

    const placeDuplicateAtRowStartMinX = (rowWorldZ: number) => {
        cabinet.position.set(sourceCabinet.position.x, sourceWorldY, rowWorldZ);
        cabinet.updateMatrixWorld(true);
        if (room) {
            alignDuplicateMinXToWorldX(roomMinXEff);
        }
    };

    const placeDuplicateAtRowEndMaxX = (rowWorldZ: number) => {
        cabinet.position.set(sourceCabinet.position.x, sourceWorldY, rowWorldZ);
        cabinet.updateMatrixWorld(true);
        if (room) {
            alignDuplicateMaxXToWorldX(roomMaxXEff);
        }
    };

    const footprintExceedsRoomZExtents = (footprintBounds: THREE.Box3) => {
        if (!room) return false;
        return (
            footprintBounds.min.z < duplicateRoomMinZEff ||
            footprintBounds.max.z > duplicateRoomMaxZEff
        );
    };

    const footprintExceedsRoomMaxX = (footprintBounds: THREE.Box3) => {
        if (!room) return false;
        return footprintBounds.max.x > roomMaxXEff;
    };

    const footprintExceedsRoomMinX = (footprintBounds: THREE.Box3) => {
        if (!room) return false;
        return footprintBounds.min.x < roomMinXEff;
    };

    const markUserDataIfDuplicateMovedToAnotherZRow = () => {
        cabinet.userData.positionedInAnotherRow =
            Math.abs(cabinet.position.z - sourceWorldZ) > zRowChangedTolerance;
    };

    /**
     * L-shape cut-out is outside the floor mesh AABB; reject positions in the void.
     *
     * @return {boolean} False when L-room and cabinet footprint sits in the missing corner.
     */
    const isDuplicateFootprintInsideLShapeFloor = (): boolean => {
        if (!cachedLShapeLayoutForFootprint) return true;
        cabinet.updateMatrixWorld(true);
        duplicateBoundsBox.setFromObject(cabinet);
        return isLShapedFootprintFullyInsideFloor(
            duplicateBoundsBox,
            cachedLShapeLayoutForFootprint,
            lShapeFootprintTolerance
        );
    };

    /**
     * After moving to a new +Z row at min X, pack along +X next to occupants.
     *
     * @return {boolean} True when non-overlapping; false to try another strategy/row.
     */
    const packNextToOccupantsAlongPositiveX = (): boolean => {
        for (
            let slideAttempt = 0;
            slideAttempt < maxHorizontalSlideAttempts;
            slideAttempt++
        ) {
            cabinet.updateMatrixWorld(true);
            duplicateBoundsBox.setFromObject(cabinet);

            if (room) {
                if (footprintExceedsRoomMaxX(duplicateBoundsBox)) {
                    return false;
                }
                if (footprintExceedsRoomMinX(duplicateBoundsBox)) {
                    alignDuplicateMinXToWorldX(roomMinXEff);
                    continue;
                }
            }

            let rightmostOccupantMaxX = -Infinity;
            let overlapsAnotherCabinet = false;

            for (const otherBounds of occupantWorldBoxes) {
                if (!boundingBoxesOverlap3D(duplicateBoundsBox, otherBounds)) {
                    continue;
                }

                overlapsAnotherCabinet = true;
                rightmostOccupantMaxX = Math.max(
                    rightmostOccupantMaxX,
                    otherBounds.max.x
                );
            }

            if (!overlapsAnotherCabinet) {
                return true;
            }

            const slideDelta =
                rightmostOccupantMaxX +
                DUPLICATE_PLACEMENT_GAP -
                duplicateBoundsBox.min.x;
            if (Math.abs(slideDelta) < NEGLIGIBLE_TRANSLATION_EPSILON) {
                return false;
            }

            cabinet.position.x += slideDelta;
            cabinet.updateMatrixWorld(true);

            duplicateBoundsBox.setFromObject(cabinet);
            if (room && footprintExceedsRoomMaxX(duplicateBoundsBox)) {
                return false;
            }
        }

        return false;
    };

    /**
     * After aligning to max X (right side of room), pack along −X next to occupants.
     *
     * @return {boolean} True when non-overlapping; false to try another strategy/row.
     */
    const packNextToOccupantsAlongNegativeX = (): boolean => {
        for (
            let slideAttempt = 0;
            slideAttempt < maxHorizontalSlideAttempts;
            slideAttempt++
        ) {
            cabinet.updateMatrixWorld(true);
            duplicateBoundsBox.setFromObject(cabinet);

            if (room) {
                if (footprintExceedsRoomMinX(duplicateBoundsBox)) {
                    return false;
                }
                if (footprintExceedsRoomMaxX(duplicateBoundsBox)) {
                    alignDuplicateMaxXToWorldX(roomMaxXEff);
                    continue;
                }
            }

            let leftmostOccupantMinX = Infinity;
            let overlapsAnotherCabinet = false;

            for (const otherBounds of occupantWorldBoxes) {
                if (!boundingBoxesOverlap3D(duplicateBoundsBox, otherBounds)) {
                    continue;
                }

                overlapsAnotherCabinet = true;
                leftmostOccupantMinX = Math.min(
                    leftmostOccupantMinX,
                    otherBounds.min.x
                );
            }

            if (!overlapsAnotherCabinet) {
                return true;
            }

            const slideDelta =
                leftmostOccupantMinX -
                DUPLICATE_PLACEMENT_GAP -
                duplicateBoundsBox.max.x;
            if (Math.abs(slideDelta) < NEGLIGIBLE_TRANSLATION_EPSILON) {
                return false;
            }

            cabinet.position.x += slideDelta;
            cabinet.updateMatrixWorld(true);

            duplicateBoundsBox.setFromObject(cabinet);
            if (room && footprintExceedsRoomMinX(duplicateBoundsBox)) {
                return false;
            }
        }

        return false;
    };

    /**
     * Resolve overlaps by sliding +X until clear or stuck.
     *
     * @return {boolean} Whether a valid free position was found.
     */
    const resolveCollisionsSlidePositiveX = (): boolean => {
        for (
            let slideAttempt = 0;
            slideAttempt < maxHorizontalSlideAttempts;
            slideAttempt++
        ) {
            duplicateBoundsBox.setFromObject(cabinet);

            if (room) {
                if (footprintExceedsRoomZExtents(duplicateBoundsBox)) {
                    return false;
                }
                if (footprintExceedsRoomMaxX(duplicateBoundsBox)) {
                    return false;
                }
                if (footprintExceedsRoomMinX(duplicateBoundsBox)) {
                    alignDuplicateMinXToWorldX(roomMinXEff);
                    continue;
                }
            }

            let rightmostOccupantMaxX = -Infinity;
            let overlapsAnotherCabinet = false;

            for (const otherBounds of occupantWorldBoxes) {
                if (!boundingBoxesOverlap3D(duplicateBoundsBox, otherBounds)) {
                    continue;
                }

                overlapsAnotherCabinet = true;
                rightmostOccupantMaxX = Math.max(
                    rightmostOccupantMaxX,
                    otherBounds.max.x
                );
            }

            if (!overlapsAnotherCabinet) {
                duplicateBoundsBox.setFromObject(cabinet);
                if (room && footprintExceedsRoomMaxX(duplicateBoundsBox)) {
                    return false;
                }
                if (!isDuplicateFootprintInsideLShapeFloor()) {
                    return false;
                }
                markUserDataIfDuplicateMovedToAnotherZRow();
                return true;
            }

            const slideDelta =
                rightmostOccupantMaxX +
                DUPLICATE_PLACEMENT_GAP -
                duplicateBoundsBox.min.x;
            if (Math.abs(slideDelta) < NEGLIGIBLE_TRANSLATION_EPSILON) {
                return false;
            }

            cabinet.position.x += slideDelta;
            cabinet.updateMatrixWorld(true);

            duplicateBoundsBox.setFromObject(cabinet);
            if (room && footprintExceedsRoomMaxX(duplicateBoundsBox)) {
                return false;
            }
        }

        return false;
    };

    /**
     * Resolve overlaps by sliding −X until clear or stuck.
     *
     * @return {boolean} Whether a valid free position was found.
     */
    const resolveCollisionsSlideNegativeX = (): boolean => {
        for (
            let slideAttempt = 0;
            slideAttempt < maxHorizontalSlideAttempts;
            slideAttempt++
        ) {
            duplicateBoundsBox.setFromObject(cabinet);

            if (room) {
                if (footprintExceedsRoomZExtents(duplicateBoundsBox)) {
                    return false;
                }
                if (footprintExceedsRoomMinX(duplicateBoundsBox)) {
                    return false;
                }
                if (footprintExceedsRoomMaxX(duplicateBoundsBox)) {
                    alignDuplicateMaxXToWorldX(roomMaxXEff);
                    continue;
                }
            }

            let leftmostOccupantMinX = Infinity;
            let overlapsAnotherCabinet = false;

            for (const otherBounds of occupantWorldBoxes) {
                if (!boundingBoxesOverlap3D(duplicateBoundsBox, otherBounds)) {
                    continue;
                }

                overlapsAnotherCabinet = true;
                leftmostOccupantMinX = Math.min(
                    leftmostOccupantMinX,
                    otherBounds.min.x
                );
            }

            if (!overlapsAnotherCabinet) {
                duplicateBoundsBox.setFromObject(cabinet);
                if (room && footprintExceedsRoomMinX(duplicateBoundsBox)) {
                    return false;
                }
                if (!isDuplicateFootprintInsideLShapeFloor()) {
                    return false;
                }
                markUserDataIfDuplicateMovedToAnotherZRow();
                return true;
            }

            const slideDelta =
                leftmostOccupantMinX -
                DUPLICATE_PLACEMENT_GAP -
                duplicateBoundsBox.max.x;
            if (Math.abs(slideDelta) < NEGLIGIBLE_TRANSLATION_EPSILON) {
                return false;
            }

            cabinet.position.x += slideDelta;
            cabinet.updateMatrixWorld(true);

            duplicateBoundsBox.setFromObject(cabinet);
            if (room && footprintExceedsRoomMinX(duplicateBoundsBox)) {
                return false;
            }
        }

        return false;
    };

    /**
     * Right-biased attempt at `rowWorldZ`: beside source on row 0, else min-X row + pack +X.
     *
     * @param {number} rowWorldZ World Z for this row.
     * @param {boolean} anchorImmediatelyEastOfSource When true, place flush +X of the source (first row only).
     * @return {boolean} Whether placement succeeded.
     */
    const tryPositiveXFlowAtWorldZ = (
        rowWorldZ: number,
        anchorImmediatelyEastOfSource: boolean
    ): boolean => {
        if (anchorImmediatelyEastOfSource) {
            placeDuplicateRightOfSourceOnRow(rowWorldZ);
        } else {
            placeDuplicateAtRowStartMinX(rowWorldZ);
            if (!packNextToOccupantsAlongPositiveX()) {
                return false;
            }
        }

        cabinet.updateMatrixWorld(true);
        duplicateBoundsBox.setFromObject(cabinet);
        if (room && footprintExceedsRoomZExtents(duplicateBoundsBox)) {
            return false;
        }

        return resolveCollisionsSlidePositiveX();
    };

    /**
     * Left-biased attempt at `rowWorldZ`: left of source on row 0, else max-X row + pack −X.
     *
     * @param {number} rowWorldZ World Z for this row.
     * @param {boolean} anchorImmediatelyWestOfSource When true, place flush −X of the source (first row only).
     * @return {boolean} Whether placement succeeded.
     */
    const tryNegativeXFlowAtWorldZ = (
        rowWorldZ: number,
        anchorImmediatelyWestOfSource: boolean
    ): boolean => {
        if (anchorImmediatelyWestOfSource) {
            placeDuplicateLeftOfSourceOnRow(rowWorldZ);
        } else {
            placeDuplicateAtRowEndMaxX(rowWorldZ);
            if (!packNextToOccupantsAlongNegativeX()) {
                return false;
            }
        }

        cabinet.updateMatrixWorld(true);
        duplicateBoundsBox.setFromObject(cabinet);
        if (room && footprintExceedsRoomZExtents(duplicateBoundsBox)) {
            return false;
        }

        return resolveCollisionsSlideNegativeX();
    };

    // Pass 1 — +Z “forward” rows: right of source, then min-X starts, sliding +X into free space.
    for (let rowIndex = 0; rowIndex < maxWorldZRowAttempts; rowIndex++) {
        const rowWorldZ = sourceWorldZ + rowIndex * rowSpacingAlongWorldZ;
        if (tryPositiveXFlowAtWorldZ(rowWorldZ, rowIndex === 0)) {
            return true;
        }
    }

    // Pass 2 — +Z rows: left of source, then max-X starts, sliding −X.
    for (let rowIndex = 0; rowIndex < maxWorldZRowAttempts; rowIndex++) {
        const rowWorldZ = sourceWorldZ + rowIndex * rowSpacingAlongWorldZ;
        if (tryNegativeXFlowAtWorldZ(rowWorldZ, rowIndex === 0)) {
            return true;
        }
    }

    // Pass 3 — −Z “back” rows: try −X bias first, then +X bias (no “beside source” anchors).
    for (
        let backRowStep = 1;
        backRowStep <= maxWorldZRowAttempts;
        backRowStep++
    ) {
        const rowWorldZ = sourceWorldZ - backRowStep * rowSpacingAlongWorldZ;
        if (tryNegativeXFlowAtWorldZ(rowWorldZ, false)) {
            return true;
        }
        if (tryPositiveXFlowAtWorldZ(rowWorldZ, false)) {
            return true;
        }
    }

    return false;
};

/**
 * Wall-mounted (upper) duplicate: snap behind wall, pack along the wall line, then walk
 * clockwise to the next wall segment when still blocked. Rows step vertically by cabinet height.
 *
 * @return {boolean} Whether the duplicate was placed without overlap.
 */
export const duplicateUpperCabinet = ({
    cabinet,
    sourceCabinet,
    existingCabinets,
    scene,
    roomType,
    firstRowStartPosition,
}: PlaceCabinetWithWallFallbackParams): boolean => {
    existingCabinets.forEach((neighborCabinet) => {
        if (!neighborCabinet.userData.currentWall) {
            neighborCabinet.userData.currentWall =
                (getWallBehindCabinet(
                    neighborCabinet,
                    scene,
                    roomType as RoomType
                ) as THREE.Mesh | null) ?? null;
        }
    });

    const sourceWall =
        sourceCabinet.userData.currentWall ??
        (getWallBehindCabinet(
            sourceCabinet,
            scene,
            roomType as RoomType
        ) as THREE.Mesh | null);

    const wallMeshes = getWallMeshes(scene);
    const maxWallAttempts = Math.max(1, wallMeshes.length);
    const sourceWallKey = getWallKey(sourceWall ?? null, roomType);
    const sourceDirection = sourceCabinet.userData.currentDirection || 'front';

    const sourceAnchorWorldY = sourceCabinet.position.y;
    const duplicateLocalBounds = new THREE.Box3().setFromObject(cabinet);
    const duplicateSize = duplicateLocalBounds.getSize(new THREE.Vector3());
    const upperRowHeightWorld = Math.max(1, duplicateSize.y, duplicateSize.z);
    const verticalRowSpacing = upperRowHeightWorld + DUPLICATE_PLACEMENT_GAP;
    const maxVerticalRowAttempts = 13;

    for (
        let verticalRowIndex = 0;
        verticalRowIndex < maxVerticalRowAttempts;
        verticalRowIndex++
    ) {
        const targetRowWorldY =
            sourceAnchorWorldY + verticalRowIndex * verticalRowSpacing;
        const rowStartPosition =
            verticalRowIndex === 0
                ? firstRowStartPosition
                : sourceCabinet.position;
        const deltaFallbackMovement = getMovementFromDelta(
            sourceCabinet.position,
            rowStartPosition,
            sourceDirection
        );
        const initialMovement = getPlacementMovementForWallKey(
            sourceWallKey,
            deltaFallbackMovement
        );

        cabinet.position.copy(rowStartPosition);
        cabinet.position.y = targetRowWorldY;
        cabinet.rotation.copy(sourceCabinet.rotation);
        cabinet.userData.currentDirection =
            sourceCabinet.userData.currentDirection;
        cabinet.userData.currentWall = sourceWall ?? null;

        let activeWall = cabinet.userData.currentWall ?? null;
        let wallAttempts = 0;

        let placed = resolvePositionOnWall({
            cabinet,
            existingCabinets,
            wall: activeWall,
            roomType,
            isUpperProduct: Boolean(cabinet.userData.isUpperProduct),
            movementOverride: initialMovement,
        });

        while (!placed && wallAttempts < maxWallAttempts) {
            const nextWall = getNextWallOnRight(
                activeWall,
                getMovementFromDirection(cabinet.userData.currentDirection)
                    .vector,
                wallMeshes,
                roomType
            );

            if (!nextWall) {
                break;
            }

            const jumpResult = tryPlaceAfterWallJump({
                cabinet,
                existingCabinets,
                activeWall,
                nextWall,
                roomType,
                targetRowY: targetRowWorldY,
                sourceWallKey,
                sourceDirection,
            });

            placed = jumpResult.placed;
            activeWall = jumpResult.activeWall;
            wallAttempts += 1;
        }

        if (placed) {
            const {clamped} = autoRotateClamping(
                cabinet,
                activeWall,
                cabinet.position.clone(),
                true,
                roomType
            );
            cabinet.position.copy(clamped);
            cabinet.updateMatrixWorld(true);
            return true;
        }
    }

    return false;
};

/**
 * Place a brand-new (non-duplicate) upper cabinet on the wall closest to `initialPosition`,
 * then walk clockwise around walls if the initial wall is fully occupied.
 *
 * Unlike `duplicateUpperCabinet` this requires no source cabinet — the wall and slide
 * direction are derived purely from `initialPosition` and the scene geometry.
 *
 * @param {CabinetMesh} cabinet Cabinet mesh to place (already scaled + rotated).
 * @param {CabinetMesh[]} existingCabinets Other cabinets already in the scene.
 * @param {THREE.Vector3} initialPosition World position used to find the nearest wall.
 * @param {THREE.Scene} scene Active room-planner scene.
 * @param {string} roomType Room shape key.
 * @return {boolean} Whether a non-overlapping wall position was found.
 */
export const placeNewUpperCabinetOnWall = ({
    cabinet,
    existingCabinets,
    initialPosition,
    scene,
    roomType,
}: {
    cabinet: CabinetMesh;
    existingCabinets: CabinetMesh[];
    initialPosition: THREE.Vector3;
    scene: THREE.Scene;
    roomType: string;
}): boolean => {
    const wallMeshes = getWallMeshes(scene);
    const maxWallAttempts = Math.max(1, wallMeshes.length);

    // Always start from the back wall, found by semantic key rather than by
    // cabinet position.  Position-based detection (getWallBehindCabinet) picks
    // the wrong wall when initialPosition.x is past the room edge — which
    // happens when the back-wall row is already full and the X cursor has
    // overflowed.  Using the key guarantees the correct starting wall for both
    // RECTANGULAR ('wall:back') and LSHAPED ('rightWidthWall') rooms.
    const backWall =
        wallMeshes.find((w) => getWallKey(w, roomType) === 'back') ?? null;

    // rotation.z = 0 → direction 'normal' → cabinet faces the room from the back
    // wall.  autoRotateClamping uses this direction to pick the right Z clamp.
    // tryPlaceAfterWallJump will rotate to the next wall's angle when walking.
    cabinet.rotation.z = 0;
    cabinet.userData.currentWall = backWall;
    cabinet.userData.currentDirection = 'front';

    cabinet.position.copy(initialPosition);
    cabinet.updateMatrixWorld(true);

    const sourceWallKey: WallKey = 'back';
    const initialMovement = getPlacementMovementForWallKey(
        sourceWallKey,
        getMovementFromDirection('front')
    );

    let activeWall: THREE.Mesh | null = backWall;
    let wallAttempts = 0;

    let placed = resolvePositionOnWall({
        cabinet,
        existingCabinets,
        wall: activeWall,
        roomType,
        isUpperProduct: true,
        movementOverride: initialMovement,
    });

    while (!placed && wallAttempts < maxWallAttempts) {
        const nextWall = getNextWallOnRight(
            activeWall,
            getMovementFromDirection(cabinet.userData.currentDirection).vector,
            wallMeshes,
            roomType
        );

        if (!nextWall) break;

        const jumpResult = tryPlaceAfterWallJump({
            cabinet,
            existingCabinets,
            activeWall,
            nextWall,
            roomType,
            targetRowY: initialPosition.y,
            sourceWallKey,
            sourceDirection: 'front',
            skipInitialOffset: true,
        });

        placed = jumpResult.placed;
        activeWall = jumpResult.activeWall;
        wallAttempts += 1;
    }

    if (placed) {
        const {clamped} = autoRotateClamping(
            cabinet,
            activeWall,
            cabinet.position.clone(),
            true,
            roomType
        );
        cabinet.position.copy(clamped);
        cabinet.updateMatrixWorld(true);
    }

    return placed;
};

/**
 * Entry point for duplicate placement: floor-like products use `duplicateFloorCabinet`; uppers use
 * `duplicateUpperCabinet` (wall hop + vertical rows).
 *
 * @param {PlaceCabinetWithWallFallbackParams} params Duplicate candidate, source, peers, and scene.
 * @return {boolean} Whether a non-overlapping position was found.
 */
export const placeCabinetWithWallFallback = (
    params: PlaceCabinetWithWallFallbackParams
) => {
    if (!Boolean(params.cabinet.userData.isUpperProduct)) {
        return duplicateFloorCabinet({
            cabinet: params.cabinet,
            sourceCabinet: params.sourceCabinet,
            existingCabinets: params.existingCabinets,
            scene: params.scene,
        });
    }

    return duplicateUpperCabinet(params);
};
